/**
 * Create/edit-policy modal for the Access policy builder.
 *
 * A policy is WHO × DEVICE × WHAT → GRANT. WHO (users, groups) and WHAT
 * (applications, application groups) are searchable multi-select dropdowns — no
 * free-text entry. DEVICE is a single Posture Profile picked by name (V31); the
 * requirements themselves are authored under Secure Access → Posture Profiles.
 * Tags and L7 method/path narrowing live under Advanced. A policy is a Grant by
 * default; a Block is an explicit exception that must sit above the grant it
 * carves out of.
 *
 * Matching (policy model review §3): a request matches when the subject is in
 * the WHO set AND the device satisfies the profile AND the flow's app is in the
 * WHAT set. Applications and Application Groups are unioned (a group expands to
 * its member apps at compile). WHEN (time/geo) is compiled by the API but not
 * exposed here.
 *
 * "Require managed device" is intentionally not shown: enrolled+active is
 * already enforced at the gateway by device admission. An existing policy's
 * value is preserved on edit.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import SearchableMultiSelect, { type MultiSelectOption } from '../common/SearchableMultiSelect';
import { zodResolver } from '../../utils/zodResolver';
import type {
  AccessApp,
  AccessPolicyRule,
  AppGroup,
  CreatePolicyRuleData,
  IdentityGroup,
  PostureProfile,
} from '../../services/access.service';
import type { VPNUser } from '../../services/vpnUser.service';
import { fmtRequirement } from '../../data/postureCatalog';
import PostureWarnings from './PostureWarnings';

const ruleSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().max(512).optional(),
  priority: z.coerce.number().int().min(0).max(65535),
  action: z.enum(['allow', 'deny']),
  appTagsCsv: z.string().max(512).optional(),
  httpMethodsCsv: z.string().max(128).optional(),
  pathPrefixesCsv: z.string().max(1024).optional(),
});

type RuleFormValues = z.infer<typeof ruleSchema>;

const defaults: RuleFormValues = {
  name: '',
  description: '',
  priority: 100,
  action: 'allow',
  appTagsCsv: '',
  httpMethodsCsv: '',
  pathPrefixesCsv: '',
};

/** Sentinel for the Device picker: a pre-profile policy whose inline
 *  requirements are kept as-is until the admin picks a profile (or "Any"). */
const LEGACY_INLINE = '__legacy__';

function csv(value?: string): string[] | undefined {
  const parts = (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : undefined;
}

/** Append any selected values missing from `base` as bare options, so a
 *  selection outside the current directory (e.g. an external-IdP group, a
 *  deleted app) still shows as a chip and is never silently dropped. */
function withUnknowns(base: MultiSelectOption[], selected: string[]): MultiSelectOption[] {
  const known = new Set(base.map((o) => o.value));
  const extras = selected.filter((v) => !known.has(v)).map((v) => ({ value: v, label: v }));
  return [...base, ...extras];
}

function ruleToFormValues(rule: AccessPolicyRule): RuleFormValues {
  return {
    name: rule.name,
    description: rule.description ?? '',
    priority: rule.priority,
    action: rule.action,
    appTagsCsv: (rule.app_tags ?? []).join(', '),
    httpMethodsCsv: (rule.http_methods ?? []).join(', '),
    pathPrefixesCsv: (rule.path_prefixes ?? []).join(', '),
  };
}

interface PolicyRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreatePolicyRuleData) => Promise<void>;
  /** When set, the modal edits this policy in place; otherwise it creates a new one. */
  rule?: AccessPolicyRule | null;
  /** The tenant's identity groups — the "Who → Groups" picker source. */
  identityGroups?: IdentityGroup[];
  /** The tenant's VPN users — the "Who → Users" picker source. */
  vpnUsers?: VPNUser[];
  /** The tenant's apps — the "What → Applications" picker source. */
  apps?: AccessApp[];
  /** The tenant's application groups — the "What → Application Groups" picker source. */
  appGroups?: AppGroup[];
  /** The tenant's posture profiles — the "Device" picker source. */
  postureProfiles?: PostureProfile[];
}

const PolicyRuleModal = ({
  isOpen,
  onClose,
  onSave,
  rule = null,
  identityGroups = [],
  vpnUsers = [],
  apps = [],
  appGroups = [],
  postureProfiles = [],
}: PolicyRuleModalProps) => {
  const editing = !!rule;
  const initialValues = rule ? ruleToFormValues(rule) : defaults;

  // Neither "require managed device" nor "enabled" is editable in this modal:
  // a new policy is enabled by default, and both an existing policy's values
  // are preserved on save (enable/disable is toggled from the policies list).
  const seededManaged = rule?.require_managed_device === 1;
  const seededEnabled = rule ? rule.enabled === 1 : true;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RuleFormValues>({
    resolver: zodResolver<RuleFormValues>(ruleSchema),
    defaultValues: initialValues,
  });

  // A policy authored before profiles existed carries inline requirements. They
  // stay enforced until the admin explicitly replaces them (profile or "Any").
  const legacyInline =
    !!rule && !rule.posture_profile_id && !!rule.device_posture && Object.keys(rule.device_posture).length > 0;

  // WHO / DEVICE / WHAT picker selections live outside RHF. The parent mounts
  // a fresh modal per target (keyed), so seeding at mount is enough.
  const [pickedGroups, setPickedGroups] = useState<string[]>(() => rule?.subject_groups ?? []);
  const [pickedUsers, setPickedUsers] = useState<string[]>(() => rule?.subject_users ?? []);
  const [pickedProfile, setPickedProfile] = useState<string>(() =>
    rule?.posture_profile_id ?? (legacyInline ? LEGACY_INLINE : ''),
  );
  const [pickedApps, setPickedApps] = useState<string[]>(() => rule?.app_ids ?? []);
  const [pickedAppGroups, setPickedAppGroups] = useState<string[]>(() => rule?.app_group_ids ?? []);

  // Options for the four multi-select dropdowns, each carrying any selected-but-unknown values.
  const groupOptions = withUnknowns(
    identityGroups.map((g) => ({ value: g.name, label: g.name, hint: `${g.memberCount} member${g.memberCount === 1 ? '' : 's'}` })),
    pickedGroups,
  );
  const userOptions = withUnknowns(
    vpnUsers.map((u) => ({ value: u.email, label: u.displayName || u.email, hint: u.email })),
    pickedUsers,
  );
  const appOptions = withUnknowns(
    apps.map((a) => ({ value: a.id, label: a.display_name || a.name, hint: a.app_type })),
    pickedApps,
  );
  const appGroupOptions = withUnknowns(
    appGroups.map((g) => ({ value: g.id, label: g.name, hint: `${g.memberCount} app${g.memberCount === 1 ? '' : 's'}` })),
    pickedAppGroups,
  );

  const selectedProfile = postureProfiles.find((p) => p.id === pickedProfile) ?? null;
  // A referenced profile missing from the directory (e.g. list call failed)
  // still renders so the reference is never silently dropped on save.
  const profileUnknown = !!pickedProfile && pickedProfile !== LEGACY_INLINE && !selectedProfile;
  const requirementEntries = selectedProfile
    ? Object.entries(selectedProfile.requirements ?? {})
    : pickedProfile === LEGACY_INLINE
      ? Object.entries(rule?.device_posture ?? {})
      : [];

  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    () =>
      !!rule &&
      ((rule.app_tags ?? []).length > 0 ||
        (rule.http_methods ?? []).length > 0 ||
        (rule.path_prefixes ?? []).length > 0),
  );

  const clearManual = () => {
    setPickedGroups([]);
    setPickedUsers([]);
    setPickedProfile('');
    setPickedApps([]);
    setPickedAppGroups([]);
  };

  const handleClose = () => {
    clearManual();
    onClose();
  };

  const submit = handleSubmit(async (values) => {
    // DEVICE: a profile id, null for "Any device", or untouched when the legacy
    // inline requirements are kept. Picking a profile or "Any" on a legacy
    // policy clears the inline map so the two can never disagree.
    const keepLegacy = pickedProfile === LEGACY_INLINE;
    const postureProfileId = keepLegacy ? undefined : pickedProfile || null;
    const devicePosture = legacyInline && !keepLegacy ? {} : undefined;

    await onSave({
      name: values.name,
      description: values.description || undefined,
      priority: values.priority,
      enabled: seededEnabled,
      action: values.action,
      subjectGroups: pickedGroups.length > 0 ? pickedGroups : undefined,
      subjectUsers: pickedUsers.length > 0 ? pickedUsers : undefined,
      requireManagedDevice: seededManaged,
      postureProfileId,
      devicePosture,
      appIds: pickedApps.length > 0 ? pickedApps : undefined,
      appGroupIds: pickedAppGroups.length > 0 ? pickedAppGroups : undefined,
      appTags: csv(values.appTagsCsv),
      httpMethods: csv(values.httpMethodsCsv)?.map((m) => m.toUpperCase()),
      pathPrefixes: csv(values.pathPrefixesCsv),
    });
    clearManual();
  });

  const whoCount = pickedGroups.length + pickedUsers.length;
  const whatCount = pickedApps.length + pickedAppGroups.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editing ? 'Edit Policy' : 'Add Policy'}
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Policy'}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <AccessFormField label="Name" htmlFor="rule-name" required error={errors.name?.message}>
              <input
                id="rule-name"
                className="input-base w-full"
                placeholder="Engineering to internal apps"
                {...register('name')}
              />
            </AccessFormField>
          </div>
          <AccessFormField
            label="Priority"
            htmlFor="rule-priority"
            required
            error={errors.priority?.message}
            hint="Lower runs first."
          >
            <input id="rule-priority" type="number" className="input-base w-full" {...register('priority')} />
          </AccessFormField>
        </div>

        <AccessFormField label="Description" htmlFor="rule-desc" error={errors.description?.message}>
          <input id="rule-desc" className="input-base w-full" {...register('description')} />
        </AccessFormField>

        <AccessFormField
          label="Action"
          htmlFor="rule-action"
          required
          error={errors.action?.message}
          hint="A block is an exception: place it ABOVE the grant it carves out of (first match wins; tenant default is deny)."
        >
          <select id="rule-action" className="input-base w-full" {...register('action')}>
            <option value="allow">Grant access</option>
            <option value="deny">Block (exception)</option>
          </select>
        </AccessFormField>

        {/* WHO — searchable multi-select over the tenant directory. A request's
            subject matches when it is one of the selected users or in one of the
            selected groups. Empty means any. */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Who</p>
          <div className="grid grid-cols-2 gap-4">
            <AccessFormField
              label="Groups"
              htmlFor="rule-groups"
              hint={pickedGroups.length > 0 ? `${pickedGroups.length} selected. Empty means any.` : 'Empty means any.'}
            >
              <SearchableMultiSelect
                id="rule-groups"
                options={groupOptions}
                selected={pickedGroups}
                onChange={setPickedGroups}
                placeholder="Any group"
                searchPlaceholder="Search groups…"
                emptyText="No user groups yet — create them under Administration → User Groups."
              />
            </AccessFormField>
            <AccessFormField
              label="Users"
              htmlFor="rule-users"
              hint={pickedUsers.length > 0 ? `${pickedUsers.length} selected. Empty means any.` : 'Empty means any.'}
            >
              <SearchableMultiSelect
                id="rule-users"
                options={userOptions}
                selected={pickedUsers}
                onChange={setPickedUsers}
                placeholder="Any user"
                searchPlaceholder="Search users…"
                emptyText="No users in this tenant yet."
              />
            </AccessFormField>
          </div>
          {whoCount === 0 && (
            <p className="mt-2 text-xs text-gray-500">Nothing selected — this policy matches any subject.</p>
          )}
        </div>

        {/* DEVICE — one Posture Profile by name. The requirements live on the
            profile (Secure Access → Posture Profiles), not on the policy. */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Device</p>
          <AccessFormField
            label="Posture profile"
            htmlFor="rule-posture-profile"
            hint={
              postureProfiles.length === 0 && !legacyInline
                ? 'No profiles yet — create them under Secure Access → Posture Profiles.'
                : 'The device must satisfy every requirement of the profile. "Any device" applies no posture check.'
            }
          >
            <select
              id="rule-posture-profile"
              className="input-base w-full"
              value={pickedProfile}
              onChange={(e) => setPickedProfile(e.target.value)}
            >
              <option value="">Any device</option>
              {legacyInline && <option value={LEGACY_INLINE}>Inline requirements (legacy)</option>}
              {profileUnknown && <option value={pickedProfile}>{pickedProfile} (unknown profile)</option>}
              {postureProfiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.description ? ` — ${p.description}` : ''}
                </option>
              ))}
            </select>
          </AccessFormField>

          {requirementEntries.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid="rule-posture-requirements">
              {requirementEntries.map(([k, v]) => (
                <span
                  key={k}
                  className="inline-flex items-center rounded bg-teal-50 border border-teal-200 px-1.5 py-0.5 text-[11px] text-teal-700"
                >
                  {fmtRequirement(k, v)}
                </span>
              ))}
            </div>
          )}
          {requirementEntries.length > 0 && (
            <PostureWarnings
              requirements={Object.fromEntries(requirementEntries)}
              subject="this policy's posture requirements"
              testId="rule-posture-warnings"
            />
          )}
          {pickedProfile === LEGACY_INLINE && (
            <p className="mt-2 text-xs text-amber-600">
              This policy was authored with inline requirements. They stay enforced as-is; pick a profile (or "Any
              device") to replace them.
            </p>
          )}
          {pickedProfile === '' && (
            <p className="mt-2 text-xs text-gray-500">
              No posture check — any enrolled device that passes gateway admission matches.
            </p>
          )}
        </div>

        {/* WHAT — searchable multi-select over apps + application groups. The two
            are unioned (a group expands to its member apps at compile). Empty
            means any app. */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">What</p>
          <div className="grid grid-cols-2 gap-4">
            <AccessFormField
              label="Applications"
              htmlFor="rule-apps"
              hint={pickedApps.length > 0 ? `${pickedApps.length} selected. Empty means any app.` : 'Empty means any app.'}
            >
              <SearchableMultiSelect
                id="rule-apps"
                options={appOptions}
                selected={pickedApps}
                onChange={setPickedApps}
                placeholder="Any application"
                searchPlaceholder="Search applications…"
                emptyText="No applications defined yet."
              />
            </AccessFormField>
            <AccessFormField
              label="Application Groups"
              htmlFor="rule-appgroups"
              hint={pickedAppGroups.length > 0 ? `${pickedAppGroups.length} selected.` : 'Manage groups under Applications → Groups.'}
            >
              <SearchableMultiSelect
                id="rule-appgroups"
                options={appGroupOptions}
                selected={pickedAppGroups}
                onChange={setPickedAppGroups}
                placeholder="No group"
                searchPlaceholder="Search application groups…"
                emptyText="No application groups yet — create them under Applications → Groups."
              />
            </AccessFormField>
          </div>

          {whatCount === 0 && (
            <p className="mt-2 text-xs text-amber-600">
              Nothing selected — this policy matches <span className="font-medium">any application</span>.
            </p>
          )}

          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800"
            onClick={() => setShowAdvanced((s) => !s)}
          >
            {showAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Advanced — tags &amp; L7 narrowing
          </button>
          {showAdvanced && (
            <div className="mt-2 grid grid-cols-3 gap-4">
              <AccessFormField
                label="App Tags"
                htmlFor="rule-tags"
                error={errors.appTagsCsv?.message}
                hint="Match apps carrying a tag. Combined with the picks above."
              >
                <input id="rule-tags" className="input-base w-full" placeholder="internal" {...register('appTagsCsv')} />
              </AccessFormField>
              <AccessFormField
                label="HTTP Methods"
                htmlFor="rule-methods"
                error={errors.httpMethodsCsv?.message}
                hint="L7 (HTTP/HTTPS) apps only."
              >
                <input id="rule-methods" className="input-base w-full" placeholder="GET, POST" {...register('httpMethodsCsv')} />
              </AccessFormField>
              <AccessFormField
                label="Path Prefixes"
                htmlFor="rule-paths"
                error={errors.pathPrefixesCsv?.message}
                hint="L7 (HTTP/HTTPS) apps only."
              >
                <input id="rule-paths" className="input-base w-full" placeholder="/browse" {...register('pathPrefixesCsv')} />
              </AccessFormField>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default PolicyRuleModal;
